// src/components/financials/ReportsCenter.js
// Updated: 01/25/2026 - Swapped "Tax Statements" to use the Bulk Generator

import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  Table, TableBody, TableCell, TableRow, 
  Divider, CircularProgress, Stack
} from '@mui/material';
import { Print, Assessment, AccountBalance, DonutLarge, ReceiptLong, AttachMoney } from '@mui/icons-material'; 
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

// Import Reports
import GivingAnalysisReport from './GivingAnalysisReport';
import ContributionStatementGenerator from './ContributionStatementGenerator'; // <--- FIXED IMPORT (Was DonorTaxStatements)
import FundActivityReport from './FundActivityReport'; 

const ReportsCenter = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('activities'); 
  
  // DYNAMIC YEAR LOGIC
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);

  const [financialData, setFinancialData] = useState({
    income: [],
    expenses: [],
    funds: [],
    liabilities: [],
    assets: [] 
  });

  const churchId = userProfile?.churchId;

  const runReport = async () => {
    if (!churchId) return;
    setLoading(true);

    try {
      // 1. Fetch Funds, Liabilities, and Fixed Assets
      const fundsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
      const liabSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'liabilities'));
      const assetSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'assets'));
      
      const funds = fundsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const liabilities = liabSnap.docs.map(d => d.data());
      const assets = assetSnap.docs.map(d => d.data());

      // 2. Fetch Income (Transactions)
      const incQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
        where('status', '==', 'posted'),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      const incSnap = await getDocs(incQ);
      const income = incSnap.docs.map(d => d.data());

      // 3. Fetch Expenses (Expenses)
      const expQ = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'expenses'),
        where('status', '==', 'approved'),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      const expSnap = await getDocs(expQ);
      const expenses = expSnap.docs.map(d => d.data());

      setFinancialData({ income, expenses, funds, liabilities, assets });
    } catch (e) {
      console.error(e);
      alert("Error generating report: " + e.message);
    }
    setLoading(false);
  };

  const renderStatementOfActivities = () => {
    const incomeByFund = {};
    let totalIncome = 0;
    financialData.income.forEach(tx => {
        if (tx.splits && tx.splits.length > 0) {
            tx.splits.forEach(split => {
                const fName = split.fundName || "General";
                incomeByFund[fName] = (incomeByFund[fName] || 0) + (Number(split.amount) || 0);
                totalIncome += (Number(split.amount) || 0);
            });
        } else {
            const fName = financialData.funds.find(f => f.id === tx.fundId)?.name || "Uncategorized";
            incomeByFund[fName] = (incomeByFund[fName] || 0) + (Number(tx.amount) || 0);
            totalIncome += (Number(tx.amount) || 0);
        }
    });

    const expenseByCategory = {};
    let totalExpense = 0;
    financialData.expenses.forEach(exp => {
        const cat = exp.category || "Uncategorized";
        expenseByCategory[cat] = (expenseByCategory[cat] || 0) + (Number(exp.amount) || 0);
        totalExpense += (Number(exp.amount) || 0);
    });

    const netIncome = totalIncome - totalExpense;

    return (
        <Paper sx={{ p: 4, mt: 2 }} id="printable-report">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold">Statement of Activities</Typography>
                <Typography variant="subtitle1">For period {startDate} to {endDate}</Typography>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>Revenue (In)</Typography>
                    <Table size="small">
                        <TableBody>
                            {Object.entries(incomeByFund).map(([fund, amount]) => (
                                <TableRow key={fund}>
                                    <TableCell>{fund}</TableCell>
                                    <TableCell align="right">${amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Revenue</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalIncome.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>Expenses (Out)</Typography>
                    <Table size="small">
                        <TableBody>
                            {Object.entries(expenseByCategory).map(([cat, amount]) => (
                                <TableRow key={cat}>
                                    <TableCell>{cat}</TableCell>
                                    <TableCell align="right">${amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#fef2f2' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Expenses</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalExpense.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Paper elevation={0} variant="outlined" sx={{ p: 2, minWidth: 200, bgcolor: netIncome >= 0 ? '#f0fdf4' : '#fef2f2' }}>
                    <Typography variant="subtitle2" color="textSecondary">Net Change in Assets</Typography>
                    <Typography variant="h4" color={netIncome >= 0 ? "success.main" : "error.main"}>
                        {netIncome >= 0 ? "+" : ""}${netIncome.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </Typography>
                </Paper>
            </Box>
        </Paper>
    );
  };

  const renderStatementOfPosition = () => {
    const fundAssets = financialData.funds.reduce((acc, f) => acc + (Number(f.balance) || 0), 0);
    const fixedAssets = (financialData.assets || []).reduce((acc, a) => acc + (Number(a.cost) || 0), 0);
    const totalAssets = fundAssets + fixedAssets;

    const totalLiabilities = (financialData.liabilities || []).reduce((acc, l) => acc + (Number(l.amount) || 0), 0);
    const netAssetsValue = totalAssets - totalLiabilities;

    return (
        <Paper sx={{ p: 4, mt: 2 }} id="printable-report">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold">Statement of Financial Position</Typography>
                <Typography variant="subtitle1">As of {new Date().toLocaleDateString()}</Typography>
            </Box>

            <Grid container spacing={4}>
                {/* ASSETS */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>Assets (What we have)</Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow><TableCell colSpan={2} sx={{ fontWeight: 'bold', pt: 1 }}>Current Assets (Cash)</TableCell></TableRow>
                            {financialData.funds.map(f => (
                                <TableRow key={f.id}>
                                    <TableCell sx={{ pl: 3 }}>{f.name}</TableCell>
                                    <TableCell align="right">${(f.balance || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Cash Assets</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${fundAssets.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>

                            <TableRow><TableCell colSpan={2} sx={{ fontWeight: 'bold', pt: 3 }}>Fixed Assets (Property)</TableCell></TableRow>
                            {financialData.assets.map((a, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ pl: 3 }}>{a.name} ({a.type})</TableCell>
                                    <TableCell align="right">${(a.cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                                </TableRow>
                            ))}
                            {financialData.assets.length === 0 && (
                                <TableRow><TableCell colSpan={2} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No property recorded.</TableCell></TableRow>
                            )}
                            <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Fixed Assets</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${fixedAssets.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>

                            <TableRow sx={{ bgcolor: '#1a237e', color: 'white' }}>
                                <TableCell sx={{ fontWeight: 'bold', color: 'inherit' }}>TOTAL ASSETS</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'inherit' }}>${totalAssets.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Grid>

                {/* LIABILITIES & NET ASSETS */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>Liabilities & Net Assets</Typography>
                    <Table size="small">
                        <TableBody>
                            <TableRow><TableCell colSpan={2} sx={{ fontWeight: 'bold', pt: 1 }}>Liabilities (What we owe)</TableCell></TableRow>
                            {financialData.liabilities.map((l, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ pl: 3 }}>{l.name}</TableCell>
                                    <TableCell align="right">${(l.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                                </TableRow>
                            ))}
                            {financialData.liabilities.length === 0 && (
                                <TableRow><TableCell colSpan={2} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No debts recorded.</TableCell></TableRow>
                            )}

                            <TableRow sx={{ bgcolor: '#fef2f2' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Liabilities</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalLiabilities.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>

                            <TableRow><TableCell colSpan={2} sx={{ fontWeight: 'bold', pt: 3 }}>Net Assets (Equity)</TableCell></TableRow>
                            <TableRow>
                                <TableCell sx={{ pl: 3 }}>Unrestricted & Restricted Net Assets</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${netAssetsValue.toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>
                            
                            <TableRow sx={{ bgcolor: '#eee' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Liabilities & Net Assets</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>${(totalLiabilities + netAssetsValue).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#e8eaf6', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                            * Note: Total Assets must equal Total Liabilities + Net Assets.
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Paper sx={{ p: 3, mb: 3, '@media print': { display: 'none' } }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={1} sx={{overflowX: 'auto', pb: 1}}>
                    <Button 
                        variant={reportType === 'activities' ? 'contained' : 'outlined'} 
                        startIcon={<Assessment />}
                        onClick={() => setReportType('activities')}
                    >
                        P&L
                    </Button>
                    <Button 
                        variant={reportType === 'position' ? 'contained' : 'outlined'} 
                        startIcon={<AccountBalance />}
                        onClick={() => { setReportType('position'); runReport(); }} 
                    >
                        Balance Sheet
                    </Button>
                    
                    <Button 
                        variant={reportType === 'funds' ? 'contained' : 'outlined'} 
                        startIcon={<AttachMoney />}
                        onClick={() => setReportType('funds')} 
                    >
                        Fund Activity
                    </Button>

                    <Button 
                        variant={reportType === 'analysis' ? 'contained' : 'outlined'} 
                        startIcon={<DonutLarge />}
                        onClick={() => setReportType('analysis')} 
                    >
                        Donor Analysis
                    </Button>
                    <Button 
                        variant={reportType === 'tax' ? 'contained' : 'outlined'} 
                        startIcon={<ReceiptLong />}
                        onClick={() => setReportType('tax')} 
                    >
                        Bulk Statements
                    </Button>
                </Stack>
            </Grid>
            
            {reportType === 'activities' && (
                <>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth type="date" label="Start" InputLabelProps={{shrink:true}} value={startDate} onChange={(e)=>setStartDate(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth type="date" label="End" InputLabelProps={{shrink:true}} value={endDate} onChange={(e)=>setEndDate(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={12} sx={{mt:2, textAlign:'right'}}>
                        <Button variant="contained" size="large" onClick={runReport} disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : "Run Report"}
                        </Button>
                        <Button startIcon={<Print />} onClick={() => window.print()} sx={{ ml: 2 }}>Print</Button>
                    </Grid>
                </>
            )}
            
            {/* Show Print Button for Full Page Reports */}
            {(reportType === 'position' || reportType === 'analysis') && (
                <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                     <Button startIcon={<Print />} onClick={() => window.print()} sx={{ ml: 2 }}>Print / PDF</Button>
                </Grid>
            )}
        </Grid>
      </Paper>

      {reportType === 'activities' && (financialData.income.length > 0 || financialData.expenses.length > 0) && renderStatementOfActivities()}
      {reportType === 'position' && financialData.funds.length > 0 && renderStatementOfPosition()}
      {reportType === 'funds' && <FundActivityReport />}
      {reportType === 'analysis' && <GivingAnalysisReport />}
      
      {/* --- FIXED: Renders the Bulk Generator now --- */}
      {reportType === 'tax' && <ContributionStatementGenerator />}
      
      {!loading && reportType === 'activities' && financialData.income.length === 0 && financialData.expenses.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
              <Assessment sx={{ fontSize: 60, mb: 2, opacity: 0.5 }} />
              <Typography>Select a date range and click "Run Report" to view data.</Typography>
          </Box>
      )}
    </Box>
  );
};

export default ReportsCenter;